import { useState } from "react";
import { motion } from "motion/react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

// Import icon images
import iconHeart from "@/assets/icons/icon-heart.png";
import iconCrown from "@/assets/icons/icon-crown.png";
import iconStar from "@/assets/icons/icon-star.png";
import iconDavid from "@/assets/icons/icon-david.png";
import iconAmpersand from "@/assets/icons/icon-ampersand.png";

export interface IconData {
  iconIds: string[]; // Array of up to 2 icon IDs
}

interface IconSelectionProps {
  value: IconData | null;
  onChange: (data: IconData | null) => void;
  error?: string;
}

const ENGRAVING_ICONS = [
  { id: "heart", name: "לב", image: iconHeart },
  { id: "crown", name: "כתר", image: iconCrown },
  { id: "star", name: "כוכב", image: iconStar },
  { id: "david", name: "מגן דוד", image: iconDavid },
  { id: "ampersand", name: "&", image: iconAmpersand },
];

const MAX_ICONS = 2;
export const ICON_PRICE = 60; // Price per icon in ILS

export const IconSelection = ({
  value,
  onChange,
  error,
}: IconSelectionProps) => {
  const [selectedIcons, setSelectedIcons] = useState<string[]>(value?.iconIds || []);

  const handleIconSelect = (iconId: string) => {
    let newSelection: string[];
    
    if (selectedIcons.includes(iconId)) {
      // Remove if already selected
      newSelection = selectedIcons.filter(id => id !== iconId);
    } else if (selectedIcons.length < MAX_ICONS) {
      // Add if under limit
      newSelection = [...selectedIcons, iconId];
    } else {
      // Replace the first selected icon if at limit
      newSelection = [selectedIcons[1], iconId];
    }
    
    setSelectedIcons(newSelection);
    onChange(newSelection.length > 0 ? { iconIds: newSelection } : null);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4 p-5 bg-secondary/30 border border-border/50 shadow-sm"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-accent/20 flex items-center justify-center">
          <svg 
            className="w-4 h-4 text-foreground" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" 
            />
          </svg>
        </div>
        <div>
          <h3 className="text-base font-medium text-foreground">בחירת סמל</h3>
          <p className="text-xs text-muted-foreground">הוסף עד 2 סמלים לחריטה (אופציונלי)</p>
        </div>
      </div>

      {/* Icon Selection Grid */}
      <div className="space-y-2">
        <Label className="text-sm text-muted-foreground">
          בחר עד {MAX_ICONS} סמלים לחריטה
        </Label>
        <div className="grid grid-cols-5 gap-2">
          {ENGRAVING_ICONS.map((icon, index) => {
            const isSelected = selectedIcons.includes(icon.id);
            const selectionIndex = selectedIcons.indexOf(icon.id);
            
            return (
              <motion.button
                key={icon.id}
                type="button"
                onClick={() => handleIconSelect(icon.id)}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, delay: index * 0.03 }}
                className={cn(
                  "relative aspect-square transition-all overflow-hidden bg-background p-1.5 shadow-sm flex items-center justify-center",
                  isSelected
                    ? "ring-2 ring-primary ring-offset-1 ring-offset-background border-transparent"
                    : "border border-border/60 hover:border-primary/40 hover:shadow-md",
                  error && "ring-2 ring-destructive ring-offset-1"
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <img
                  src={icon.image}
                  alt={icon.name}
                  className="w-5 h-5 object-contain opacity-80 hover:opacity-100 transition-opacity"
                />
                {isSelected && (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-0.5 right-0.5 w-4 h-4 bg-primary flex items-center justify-center text-[10px] font-medium text-primary-foreground"
                  >
                    {selectionIndex + 1}
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
        {selectedIcons.length > 0 && (
          <p className="text-xs text-muted-foreground">
            נבחרו: {selectedIcons.map(id => ENGRAVING_ICONS.find(i => i.id === id)?.name).join(', ')}
          </p>
        )}
      </div>
      
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        הסמלים יחרטו על התכשיט ליד הטקסט
      </p>
    </motion.div>
  );
};

// Helper function to check if product has icon tag
export const hasIconTag = (tags: string[] | string | undefined): boolean => {
  if (!tags) return false;
  
  const tagList = Array.isArray(tags) 
    ? tags 
    : typeof tags === 'string' 
      ? tags.split(',').map(t => t.trim())
      : [];
  
  return tagList.some(tag => {
    const lowerTag = tag.toLowerCase().trim();
    return lowerTag === 'אייקון' || lowerTag === 'icon' || lowerTag === 'icons';
  });
};

export default IconSelection;
